// src/components/vault/documents/id-validation/AddressConfirmationForm.tsx

import React from "react";
import { Home, Loader2 } from "lucide-react";
import { Button } from "../../../ui/button";
import type { AddressConfirmationFormProps } from "./types";

export const AddressConfirmationForm: React.FC<
  AddressConfirmationFormProps
> = ({ address, onConfirm, isSubmitting = false }) => {
  // Format the address for display
  const streetLine = [
    address.calle,
    address.numeroExterior && `#${address.numeroExterior}`,
    address.numeroInterior && `Int. ${address.numeroInterior}`,
  ]
    .filter(Boolean)
    .join(" ");

  const neighborhoodLine = [
    address.colonia,
    address.codigoPostal && `C.P. ${address.codigoPostal}`,
  ]
    .filter(Boolean)
    .join(", ");

  const cityStateLine = [address.municipio, address.estado]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="flex flex-col items-center py-10 px-6 max-w-lg mx-auto">
      {/* Title */}
      <h3 className="text-xl font-semibold text-slate-900 mb-2 text-center">
        ¿Este es tu domicilio actual?
      </h3>

      {/* Description */}
      <p className="text-sm text-slate-500 text-center mb-8">
        Revisa que el domicilio detectado sea correcto y confirma si actualmente
        vives ahi.
      </p>

      {/* Address Card */}
      <div className="w-full bg-white rounded-lg p-5 mb-4 border border-slate-200">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Home
              className="w-[18px] h-[18px] text-slate-500"
              strokeWidth={1.5}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1.5">
              Domicilio detectado en tu INE
            </p>
            <p className="text-sm font-medium text-slate-900 leading-snug">
              {streetLine}
            </p>
            <p className="text-sm text-slate-500 mt-0.5">{neighborhoodLine}</p>
            <p className="text-sm text-slate-500">{cityStateLine}</p>
          </div>
        </div>
      </div>

      {/* Info text */}
      <p className="text-xs text-slate-400 mb-8 text-center leading-relaxed">
        Si el domicilio no es correcto o ya no vives en esta direccion, te
        pediremos un comprobante reciente para validar tu domicilio actual.
      </p>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3 w-full">
        <Button
          variant="outline"
          className="w-full sm:flex-1 min-w-0 min-h-11 h-auto whitespace-normal py-3 text-sm border-slate-200 text-slate-700 hover:bg-slate-50"
          onClick={() => onConfirm(false)}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Procesando...
            </>
          ) : (
            "No, usar otro domicilio"
          )}
        </Button>

        <Button
          className="w-full sm:flex-1 min-w-0 min-h-11 h-auto whitespace-normal py-3 text-sm bg-[#28397a] hover:bg-[#1e2d5f]"
          onClick={() => onConfirm(true)}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Procesando...
            </>
          ) : (
            "Sí, vivo en este domicilio"
          )}
        </Button>
      </div>
    </div>
  );
};
